import { PageType } from './analytics.types';

/**
 * Clasificación de una URL en tipo de página.
 *
 * El router no sirve para esto: una ficha de chollo no tiene ruta propia, se
 * sirve por el catch-all `:id/` (app-routing.module.ts) igual que una categoría
 * o una tienda en forma corta, y su `data.type` dice siempre 'category'. Por eso
 * aquí sólo se resuelve lo que la URL determina sin ambigüedad, y el resto queda
 * marcado como no determinista para que lo resuelva la API.
 */

/** Ruta completa (sin barras extremas) → tipo. */
export const PAGE_TYPE_BY_ROUTE: Readonly<Record<string, PageType>> = {
  '': 'home',
  'home': 'home',
  'nuevos': 'feed',
  'onfire': 'feed',
  'onfire-aliexpress': 'feed',
  'onfire-amazon': 'feed',
  'onfire-miravia': 'feed',
  'populares': 'feed',
  'comentados': 'feed',
  'para_ti': 'feed',
  'results': 'search',
  'pagina-no-encontrada': 'not_found',
  'tiendas': 'store_list',
  'categorias': 'category_list',
  'categorias/todas': 'category_list',
  'cupones': 'coupon_list',
  'grupos': 'group_list',
  'chats': 'account',
  'settings': 'account',
  'cuenta': 'account',
  'compartir-chollo': 'submit',
  'compartir-cupon': 'submit',
  'faqs': 'legal',
  'politica-de-privacidad': 'legal',
  'politica-de-cookies': 'legal',
  'aviso-legal': 'legal',
  'normas-comunitarias': 'legal',
  'reglas-de-publicacion': 'legal',
  'download-app': 'landing',
  // Portal de partners (odd/tasks/partners.md, S8): la portada es su escaparate; el resto, la cuenta del anunciante.
  'seller': 'landing',
};

/** Primer segmento → tipo, para las rutas con parámetro. */
export const PAGE_TYPE_BY_PREFIX: Readonly<Record<string, PageType>> = {
  'page': 'home',
  'tiendas': 'store',
  'cupones': 'store',
  'grupos': 'group',
  'categorias': 'category_list',
  'user': 'user',
  'chats': 'account',
  'michollo-team': 'legal',
  'reset-password': 'auth',
  'verify': 'auth',
  'seller': 'account',
  'blackfriday': 'landing',
  'price-palace': 'landing',
  'aliexpressplaza': 'landing',
  '11-11': 'landing',
  'navidad': 'landing',
  'miravia': 'landing',
  'prime-day': 'landing',
  'summer-sale': 'landing',
  'summer-days': 'landing',
  'halloween-day': 'landing',
};

export interface PageClassification {
  type: PageType;
  category?: string;
  store?: string;
  /** false ⇒ sólo lo sabe la API: es `/:id/` o `/:id/:category/` */
  deterministic: boolean;
}

/** pathname → segmentos normalizados (sin query, sin barras extremas, en minúsculas). */
export function pathSegments(pathname: string): string[] {
  const clean = (pathname || '/').split('?')[0].split('#')[0];
  return clean.split('/')
    .filter(s => !!s)
    .map(s => {
      try { return decodeURIComponent(s).toLowerCase(); } catch { return s.toLowerCase(); }
    });
}

/** Clave estable de una navegación: siempre con barra delante y detrás. */
export function normalizePath(pathname: string): string {
  const segments = pathSegments(pathname);
  return segments.length ? `/${segments.join('/')}/` : '/';
}

export function classifyPath(pathname: string): PageClassification {
  const segments = pathSegments(pathname);
  const full = segments.join('/');

  // El orden importa: replica la precedencia del router, donde el catch-all
  // `:id/` está declarado después de todas las rutas estáticas.
  const exact = PAGE_TYPE_BY_ROUTE[full];
  if (exact) return { type: exact, deterministic: true };

  const [first, second] = segments;
  const prefixed = PAGE_TYPE_BY_PREFIX[first];
  if (!prefixed) return { type: 'unknown', deterministic: false };

  // /tiendas/letter/a/ es un listado alfabético, no una tienda.
  if (first === 'tiendas' && second === 'letter') {
    return { type: 'store_list', deterministic: true };
  }

  if (prefixed === 'store' && second) {
    return { type: 'store', store: second, deterministic: true };
  }

  return { type: prefixed, deterministic: true };
}
